import { memo, ReactNode, useContext, useEffect, useState } from 'react'
import { DrawingTypes } from '../../drawingTools/drawing.interface'
import { alpha, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material'
import { ExtraSmallButton } from '../../../custom'
import { ChartEvents } from '../../chart.events'
import { AppContext } from '../../../../App'
import { useSelector } from 'react-redux'
import { selectActiveChartId } from '../../../../selectors/chart'
import '../styles.css'
import { selectMultiPaneFullScreen } from '../../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../../assets/constants'
import { useUndoRedoContext } from "../../../UndoRedoFunctonalities/UndoRedo.context";
import ThinArrowUpIcon from '../../../../assets/icons/ThinArrowUpIcon'

export type DrawingToolMenuItem = {
    text: string,
    icon: ReactNode,
    drawingToolId: DrawingTypes,
    toolOptions: Record<string, any>,
    drawingType: DrawingTypes
    code?: string
}

export type DrawingToolMenuProps = {
    items: DrawingToolMenuItem[],
    activeToolId: DrawingTypes | null,
    onClick: (drawingType: DrawingTypes, options: Record<string, any>) => void
    disable: boolean
}

const DrawingToolMenu: React.FC<DrawingToolMenuProps> = ({ items, activeToolId, onClick, disable }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const { push } = useUndoRedoContext()
    const [active, setActive] = useState(false)
    const [previousActiveMenuItem, setPreviousActiveMenuItem] = useState<DrawingToolMenuItem>(items[0])

    useEffect(() => {
        if (activeToolId === null || !items.find(i => i.drawingType === activeToolId)) {
            setActive(false)
        }
    }, [activeToolId])

    useEffect(() => {
        const chart = appContext.getChart(activeChartId)
        if(chart) chart.subscribeEvent(ChartEvents.REMOVE_DRAWING, setToolInActive)
        return ()=>{
            if(chart) chart.unsubscribeEvent(ChartEvents.REMOVE_DRAWING, setToolInActive)
        }
    }, [])

    useEffect(() => {
        if (activeToolId !== null) {
            const foundItem = items.find(i => i.drawingType === activeToolId)
            if (foundItem) {
                setPreviousActiveMenuItem(foundItem)
            }

        }
    }, [items, activeToolId])

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget)
    }

    const setToolInActive = () => {
        setActive(false)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <Stack alignItems={'center'}
            sx={{
                position: 'relative',
                '&:hover .drawing-options-menu': {
                    visibility: 'visible !important'
                }
            }}
        >
            <ExtraSmallButton
                onClick={handleClick}
                color="inherit"
                sx={{
                    visibility: Boolean(anchorEl) ? 'visible' : 'hidden',
                    bgcolor: theme => Boolean(anchorEl) ? alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity) : 'transparent',
                    position: 'absolute',
                    top: '-3px',
                    height: '10px',
                    p: 0,
                    margin: 0,
                    width: '32px',
                    zIndex: 1,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0
                }}
                disabled={disable}
                className="drawing-options-menu"
            >
                <ThinArrowUpIcon sx={{fontSize: '12px'}} />
            </ExtraSmallButton>
            <ExtraSmallButton
                color={active ? 'primary' : 'inherit'}
               
                onClick={(event) => {
                    if (active) {
                        handleClick(event)
                    } else {
                        setActive(!active)
                        onClick(previousActiveMenuItem.drawingToolId, previousActiveMenuItem.toolOptions)
                    }
                }}
                sx={{ p: 0, mt: '4px', mx: '2px' ,bgcolor: theme => active ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity) : 'transparent',}}
                disabled={disable}
            >
                {previousActiveMenuItem.icon}
            </ExtraSmallButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                elevation={3}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
            >
                {items.map((i) => (
                    <MenuItem
                        selected={i.drawingType === activeToolId}
                        key={`split-horizontal-${i.text}`}
                        onClick={() => {
                            if (activeToolId !== i.drawingType) {
                                setActive(true)
                                onClick(i.drawingType, i.toolOptions)
                            }
                            handleClose()
                        }}
                    >
                        <ListItemIcon sx={{ marginRight: '6px' }}>
                            {i.icon}
                        </ListItemIcon>
                        <ListItemText>{i.text}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    )
}

export default memo(DrawingToolMenu)